import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

// Stable style objects — extracted so recharts doesn't re-mount on every parent render.
const CHART_MARGIN = { top: 12, right: 12, left: -18, bottom: 0 };
const TOOLTIP_STYLE = {
  fontSize: 11,
  fontFamily: "IBM Plex Mono",
  borderRadius: 0,
  border: "1px solid #111",
};

function fmtDay(d) {
  const dt = new Date(d);
  return `${dt.getMonth() + 1}/${dt.getDate()}`;
}

export default function AnalyticsPanel() {
  const [a, setA] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/mine");
        setA(data);
      } catch {
        setA(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch analytics once on mount

  if (!a)
    return (
      <div className="mono p-8 text-xs text-muted-foreground">
        {a === false ? "Analytics unavailable." : "Loading analytics…"}
      </div>
    );

  const cards = [
    {
      key: "approval",
      label: "Approval rate",
      value: `${Math.round(a.approval_rate * 100)}%`,
      testid: "analytics-approval-rate",
    },
    {
      key: "ttf",
      label: "Avg time to fill",
      value:
        a.avg_time_to_fill_minutes == null
          ? "—"
          : `${a.avg_time_to_fill_minutes}m`,
      testid: "analytics-avg-ttf",
    },
    {
      key: "rev",
      label: "Revenue (approved)",
      value: `A$${Number(a.revenue_from_approved || 0).toLocaleString()}`,
      testid: "analytics-revenue",
    },
    {
      key: "total",
      label: "Total requests · 30d",
      value: String(a.total_requests || 0),
      testid: "analytics-total-requests",
    },
  ];

  const series = (a.series || []).map((p) => ({ ...p, day: fmtDay(p.day) }));

  return (
    <div className="mt-8 border border-border" data-testid="analytics-panel">
      <div className="flex items-center justify-between border-b border-border p-4 md:px-6">
        <div>
          <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
            last 30 days
          </p>
          <h2 className="text-xl tracking-tight">Performance</h2>
        </div>
      </div>

      <div className="grid grid-cols-2 border-b border-border md:grid-cols-4">
        {cards.map((c, i) => (
          <div
            key={c.key}
            data-testid={c.testid}
            className={`p-4 md:p-6 ${i < 3 ? "border-b md:border-b-0 md:border-r border-border" : ""} ${i === 0 || i === 2 ? "border-r border-border md:border-r" : ""}`}
          >
            <p className="mono text-2xl font-medium md:text-3xl">{c.value}</p>
            <p className="mono mt-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {c.label}
            </p>
          </div>
        ))}
      </div>

      <div className="p-2 md:p-4">
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <AreaChart data={series} margin={CHART_MARGIN}>
              <defs>
                <linearGradient id="totalG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A85F" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#C9A85F" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="apprG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                stroke="#a1a1aa"
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis stroke="#a1a1aa" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Area
                type="monotone"
                dataKey="total"
                name="Requests"
                stroke="#C9A85F"
                strokeWidth={2}
                fill="url(#totalG)"
              />
              <Area
                type="monotone"
                dataKey="approved"
                name="Approved"
                stroke="#059669"
                strokeWidth={2}
                fill="url(#apprG)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
